import {Component, OnInit, ChangeDetectorRef, OnDestroy, Input, ChangeDetectionStrategy} from '@angular/core';
import { Router } from '@angular/router';
import {ReplaySubject, Subscription} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from '@irb/app.service';
import { PageService } from '@irb-common/services/page.service'; 
import { IrbDashboardService } from '@irb/irb-dashboard/irb-dashboard.service';
import {IBlotterSummaryDetails, IGlobalSettings} from '@irb/irb-dashboard/irb-dashboard.model';
import { IApplyFilterOnLanding } from '@irb/app.model';
import {UtilityService} from "@irb-common/services/utility.service";

@Component({
  standalone: false,
  selector: 'irb-module-blotter',
  templateUrl: './module-blotter.component.html',
  styleUrls: ['./module-blotter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ModuleBlotterComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  data: IBlotterSummaryDetails = {
    activeBlotterTradesCount: null,
    requiresAttentionCount: null,
    pendingCount: null,
    filledCount: null,
  };
  loadedActive: boolean;
  loadedFilled: boolean;
  chartData: any;
  colors: Array<string> = [
    "#C10B14",
    "#0092CC",
    "#267326"
  ];

  options: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    cutout: '75%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    }
  };
  private masterAccountSubscription: Subscription;
  selectedMasterAccounts: Array<string> = [];
  initailLoadPassed = false;
  @Input() customizableMode: boolean = false;
  @Input() singleWidth: boolean = true;
  @Input() globalSettings: IGlobalSettings;

  constructor(
    public as: AppService,
    public ps: PageService, 
    private dbs: IrbDashboardService, 
    private cdr: ChangeDetectorRef, 
    private router: Router
  ) {}

  ngOnInit() {
    if (this.customizableMode)
      return;

    this.ps.getUserDetails().pipe(takeUntil(this.destroyed$)).subscribe((d) => {   
      if(d) {
        this.ps.deserializePageState(this.as.getFromSession("pageState"),["blotterGridDiv","blotterGridDivRes"]);

        this.masterAccountSubscription = this.dbs.currentMasterAccountData.subscribe(passedSelectedMasterAccounts => {
          if (this.initailLoadPassed) {
            this.selectedMasterAccounts = passedSelectedMasterAccounts;
            this.loadedActive = false;
            this.loadedFilled = false;
            if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
              this.getBlotterDataForAllSelectedAccounts();
            else
              this.getBlotterDataForAllAccounts();
          }
        });

        this.initailLoadPassed = true;
        this.selectedMasterAccounts = this.dbs.selectedMasterAccounts;
        if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
          this.getBlotterDataForAllSelectedAccounts();
        else
          this.getBlotterDataForAllAccounts();

      }
    });
  }

  getBlotterDataForAllAccounts() {
    this.dbs.getBlotterDetailsActive().pipe(takeUntil(this.destroyed$)).subscribe((d: any) => {
      this.data.activeBlotterTradesCount = d.activeBlotterTradesCount;
      this.data.requiresAttentionCount = d.requiresAttentionCount;
      this.data.pendingCount = d.pendingCount;
      if (this.loadedFilled) {
        this.update();
        this.cdr.detectChanges();
      }
      this.loadedActive=true;
    });
    this.dbs.getBlotterDetailsFilled().pipe(takeUntil(this.destroyed$)).subscribe((d: any) => {
      this.data.filledCount = d.filledCount;
      if (this.loadedActive) {
        this.update();
        this.cdr.detectChanges();
      }
      this.loadedFilled=true;
    });
  }

  getBlotterDataForAllSelectedAccounts() {
    this.dbs.getBlotterActiveOrdersForMasters(this.selectedMasterAccounts).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (d: any) => {
      this.data.activeBlotterTradesCount = d.activeBlotterTradesCount;
      this.data.requiresAttentionCount = d.requiresAttentionCount;
      this.data.pendingCount = d.pendingCount;
      if (this.loadedFilled) {
        this.update();
        this.cdr.detectChanges();
      }
      this.loadedActive=true;
      }
    });

    this.dbs.getBlotterFilledOrdersForMasters(this.selectedMasterAccounts).pipe(takeUntil(this.destroyed$)).subscribe({
      next: (d: any) => {
        this.data.filledCount = d.filledCount;
        if (this.loadedActive) {
          this.update();
          this.cdr.detectChanges();
        }
        this.loadedFilled = true;
      }
    });

  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.masterAccountSubscription)
      this.masterAccountSubscription.unsubscribe();
  }

  update() {
    const a = (this.data.requiresAttentionCount / this.data.activeBlotterTradesCount) * 100;
    const b = (this.data.pendingCount / this.data.activeBlotterTradesCount) * 100;
    const c = (this.data.filledCount / this.data.activeBlotterTradesCount) * 100;
    const t = a + b + c;

    this.chartData = {
      datasets: [
          {
            data: (t > 0) ? [a,b,c] : [100],
            backgroundColor: (t > 0) ? this.colors : ["#E9E9E9"],
            hoverBackgroundColor: (t > 0) ? this.colors : ["#E9E9E9"],
            borderWidth: t > 0 ? 2 : 0,
            hoverBorderColor: t > 0 ? ["#FFFFFF"] : null
          }
      ]
    };
  }

  showRequiresAttention() {
    const a = ['Error','Warning', 'Cancel rejected', 'Allocations failed', 'Cancel requested'];
    let state: IApplyFilterOnLanding = {
      isUpdatRequired: true,
      filterModel: { name: 'orderStatus', value: a, isUpdatRequired: true }
    } as IApplyFilterOnLanding;

    if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0) {
      state = {
        isUpdatRequired: true,
        filterModels: [
          { name: 'orderStatus', value: a, isUpdatRequired: true },
          {name: 'masterAccount', value: this.selectedMasterAccounts, isUpdatRequired: true}
        ]
      };
    }

    this.router.navigateByUrl('/orders/order-blotter', { state: state });
  }

  showPending() {
    const a = ['Approved', 'On hold', 'Overridden', 'Validated'];
    let state: IApplyFilterOnLanding = {
      isUpdatRequired: true,
      filterModel: { name: 'orderStatus', value: a, isUpdatRequired: true }
    } as IApplyFilterOnLanding;
    if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0) {
      state = {
        isUpdatRequired: true,
        filterModels: [
          { name: 'orderStatus', value: a, isUpdatRequired: true },
          {name: 'masterAccount', value: this.selectedMasterAccounts, isUpdatRequired: true}
        ]
      };
    }

    this.router.navigateByUrl('/orders/order-blotter', { state: state });
  }

  // showSubmitted() {
  //   const a = ['Manually submitted', 'Partially filled', 'Pending validation', 'Exported to ThinkPipes'];
  //   this.ps.pageState.blotterGridDiv["filterData"]["dom_filterOrdStatus"] = a;
  //   this.ps.pageState.blotterGridDiv["filterString"] = "orderStatus=" + a.join(",");
  //   this.ps.serializePageState();
  //   this.router.navigate(['/orders/order-blotter']);
  // }

  showFilled() {
    const a = ['Filled'];
    const d =  UtilityService.convertDateTimeToETforFilter(new Date());
    let state: IApplyFilterOnLanding = {
      isUpdatRequired: true,
      tabname:'Closed',
      filterModels: [{ name: 'orderStatus', value: a, isUpdatRequired: true },
      {name: 'modifiedDate', value: {dateFrom:d, dateTo: d}, filter: 'inRange', isUpdatRequired: true}],
    } as IApplyFilterOnLanding;
    if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0) {
      state.filterModels.push({name: 'masterAccount', value: this.selectedMasterAccounts, isUpdatRequired: true});
    }

    this.router.navigateByUrl('/orders/order-blotter', { state: state });
  }

}
